import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { X, Loader2, CreditCard, Calendar, DollarSign, Receipt, ExternalLink, MapPin, Tag } from "lucide-react";
import { PAYMENT_STATUS_META, PaymentBadge, PlanBadge, formatDate, formatMoney } from "./PaymentStatusBadge";

// Slide-in drawer that shows the full billing breakdown for an attorney.
// Loaded lazily — only fetches when `open && userId` change.
export default function AttorneyBillingDrawer({ open, userId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !userId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setData(null);
            try {
                const { data } = await api.get(`/admin/attorneys/${userId}/billing`);
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) toast.error(e?.response?.data?.detail || "Couldn't load billing.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, userId]);

    if (!open) return null;
    const summary = data?.summary;
    const placements = data?.placements || [];

    return (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="attorney-billing-drawer">
            <button
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
                aria-label="Close billing"
                data-testid="billing-drawer-backdrop"
            />
            <aside className="relative w-full max-w-xl bg-background border-l border-border overflow-y-auto shadow-2xl">
                <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-5 py-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="font-serif text-lg font-semibold truncate" data-testid="billing-drawer-title">
                            {data?.user?.name || "Attorney billing"}
                        </h2>
                        <p className="text-xs text-muted-foreground truncate">{data?.user?.email}</p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 hover:bg-accent" aria-label="Close" data-testid="billing-drawer-close">
                        <X className="h-4 w-4" />
                    </button>
                </header>

                {loading ? (
                    <div className="flex items-center justify-center py-16" data-testid="billing-drawer-loading">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : !summary ? (
                    <div className="p-6 text-sm text-muted-foreground" data-testid="billing-drawer-empty">No billing data.</div>
                ) : (
                    <div className="p-5 space-y-6">
                        {/* Headline status + plan */}
                        <section className="ll-card" data-testid="billing-headline">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <PaymentBadge status={summary.status} testid="billing-status-badge" />
                                {summary.plan_label && <PlanBadge plan={summary.plan} testid="billing-plan-badge" />}
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {PAYMENT_STATUS_META[summary.status]?.adminNote}
                            </p>
                        </section>

                        {/* KPI grid */}
                        <section className="grid grid-cols-2 gap-3" data-testid="billing-kpis">
                            <KpiCard icon={DollarSign} label="Monthly spend"
                                     value={summary.amount > 0 ? formatMoney(summary.amount) : "—"}
                                     testid="kpi-amount" />
                            <KpiCard icon={Calendar} label="Billing interval"
                                     value={summary.interval ? summary.interval[0].toUpperCase() + summary.interval.slice(1) : "—"}
                                     testid="kpi-interval" />
                            <KpiCard icon={Receipt} label="Last payment"
                                     value={formatDate(summary.last_payment_date) || "Never"}
                                     testid="kpi-last-payment" />
                            <KpiCard icon={Calendar} label="Renewal / Expiry"
                                     value={formatDate(summary.renewal_date) || "—"}
                                     testid="kpi-renewal" />
                        </section>

                        {/* Stripe references */}
                        <section className="ll-card" data-testid="billing-stripe">
                            <h3 className="ll-label mb-2 flex items-center gap-1.5">
                                <CreditCard className="h-3.5 w-3.5" /> Stripe references
                            </h3>
                            <dl className="text-xs space-y-1.5">
                                <KvRow k="Subscription ID" v={summary.stripe_subscription_id} testid="ref-sub" />
                                <KvRow k="Customer ID" v={summary.stripe_customer_id} testid="ref-cust" />
                                <KvRow k="Placements (total / active)" v={`${summary.placements_count} / ${summary.active_placements}`} testid="ref-counts" />
                            </dl>
                        </section>

                        {/* Per-placement table */}
                        <section data-testid="billing-placements">
                            <h3 className="ll-label mb-2">All placements ({placements.length})</h3>
                            {placements.length === 0 ? (
                                <div className="ll-card text-sm text-muted-foreground" data-testid="billing-placements-empty">
                                    This attorney has never purchased a placement.
                                </div>
                            ) : (
                                <ul className="space-y-2.5">
                                    {placements.map((p) => (
                                        <li key={p.id} className="ll-card" data-testid={`billing-placement-${p.id}`}>
                                            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <PlanBadge plan={p.tier} small />
                                                    <PaymentBadge status={p.status} small />
                                                    {p.is_test && (
                                                        <span className="text-[10px] rounded-full bg-amber-500/15 text-amber-700 border border-amber-500/30 px-2 py-0.5">TEST</span>
                                                    )}
                                                </div>
                                                <span className="text-xs font-semibold">{p.price_paid ? formatMoney(p.price_paid) : "—"}<span className="text-muted-foreground font-normal">/mo</span></span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-foreground mb-1">
                                                <Tag className="h-3 w-3 text-muted-foreground" />
                                                <span className="font-medium">{p.category_name || "—"}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5">
                                                <MapPin className="h-3 w-3" />
                                                {p.county_name ? `${p.county_name}${p.state_code ? `, ${p.state_code}` : ""}` : "—"}
                                            </div>
                                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                                                <span>Activated: <strong className="text-foreground">{formatDate(p.activated_at) || "—"}</strong></span>
                                                <span>Expires: <strong className="text-foreground">{formatDate(p.expires_at) || "—"}</strong></span>
                                                {p.cancelled_at && <span>Cancelled: <strong className="text-foreground">{formatDate(p.cancelled_at)}</strong></span>}
                                                {p.payment_failed_at && <span>Payment failed: <strong className="text-destructive">{formatDate(p.payment_failed_at)}</strong></span>}
                                            </div>
                                            {p.stripe_session_id && (
                                                <a className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                                                   href={`https://dashboard.stripe.com/payments/${p.stripe_session_id}`}
                                                   target="_blank" rel="noopener noreferrer">
                                                    Open in Stripe <ExternalLink className="h-3 w-3" />
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>
                )}
            </aside>
        </div>
    );
}

function KpiCard({ icon: Icon, label, value, testid }) {
    return (
        <div className="ll-card" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="text-sm font-semibold break-all">{value}</div>
        </div>
    );
}

function KvRow({ k, v, testid }) {
    return (
        <div className="flex items-start justify-between gap-3" data-testid={testid}>
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="text-foreground text-right break-all font-mono">{v || "—"}</dd>
        </div>
    );
}
